import { useCallback } from 'react';
import type { AccountApi } from '@env0/account-service/api';
import { DEFAULT_DESIGN_MODE, type DesignMode } from 'constants/config';
import { FeatureFlag } from 'constants/feature-flags';
import { useFeatureFlag, useFeatureFlags } from 'hooks/use-feature-flags';
import { useGetUserPreferences, useUpdateUserPreferences } from 'stores/rq/user-preferences';
import { markSwitchedBackToClassic, resolveDesignMode } from 'utils/design-mode.utils';
import { EventNames, track } from 'utils/analytics.utils';

export type DesignModeChangeSource = 'banner' | 'appearance-settings';

type UserPreferencesPatch = Omit<AccountApi.UpdateUserPreferences.Request.Body, 'design'>;

export const useDesignMode = () => {
  const isDesignSwitchAvailable = useFeatureFlag(FeatureFlag.UI_POLISH_NEW_DESIGN);
  const { isInitialized, didInitializationFail } = useFeatureFlags();
  const { data: preferences, isSuccess, isError } = useGetUserPreferences({ enabled: isDesignSwitchAvailable });
  const { mutate, isPending: isSaving } = useUpdateUserPreferences();

  const mode = resolveDesignMode({ isDesignSwitchAvailable, storedDesign: preferences?.design });
  const selectedDesign = preferences?.design ?? DEFAULT_DESIGN_MODE;
  const isResolved = isInitialized && !didInitializationFail && (!isDesignSwitchAvailable || isSuccess);

  // Neither failure can recover within this session: LaunchDarkly settles once, and the preferences
  // query does not retry. Whoever waits on `isResolved` should give up rather than keep waiting.
  const isResolutionBroken = didInitializationFail || isError;

  /**
   * `mutate` rather than `mutateAsync`: a failed save already raises the httpClient toast, so an
   * unhandled rejection on top of it serves nobody, and the success-only work rides `onSuccess`.
   * The tracking stays best-effort - a reload can abort it; the durable count is the `design` column.
   */
  const setMode = useCallback(
    (next: DesignMode, source: DesignModeChangeSource, alsoWrite: UserPreferencesPatch = {}) => {
      mutate(
        { design: next, ...alsoWrite },
        {
          onSuccess: () => {
            track(next === 'legacy' ? EventNames.DESIGN_OPT_OUT : EventNames.DESIGN_OPT_IN, { source });
            if (next === 'legacy') markSwitchedBackToClassic();
          }
        }
      );
    },
    [mutate]
  );

  return {
    mode,
    selectedDesign,
    isDesignSwitchAvailable,
    isResolved,
    isResolutionBroken,
    isSaving,
    preferences,
    setMode
  };
};
